import React from 'react';
import { Title, VerticalBox, Text, Loader, Box } from '../../components';
import styled from 'styled-components';
import { COLORS, device, FONTS } from '../../constants';
import usePortfolioData from '../../services/portfolio/usePortfolioData';
import { PortfolioFilters } from './partials/PortfolioFilters';
import { Updates } from '../../components/Updates/Updates';
import { usePortfolioContext } from '../../context/PortfolioContext';
import { MotionPortfolioHeaderLogo, MotionSecondaryHeaderBackground, MotionText } from '../../components/Motion';
import { Logo } from '../../assets/png';

export const Portfolio: React.FC = () => {
  const { portfolioData, error } = usePortfolioData();
  const { newProjects } = usePortfolioContext();

  if (error) {
    return (
      <Box>
        <ErrorWrapper>
          <Text text={error.message} />
        </ErrorWrapper>
      </Box>
    );
  }

  if (!portfolioData) {
    return <Loader background={'transparent'} />;
  }

  return (
    <>
      <PortfolioHeader>
        <Box>
          <VerticalBox>
            <PortfolioHeaderContent>
              <PortfolioHeaderWrapper>
                <Title
                  color={COLORS.HEADER_TEXT_COLOR}
                  title={portfolioData.portfolioHeader.Title}
                  size={60}
                  lineHeight={70}
                  weight={600}
                />
                <Text
                  text={portfolioData.portfolioHeader.Subtitle}
                  fontFamily={FONTS.SUBTITLE}
                  size={22}
                  lineHeight={28}
                  color={COLORS.HEADER_TEXT_COLOR}
                />
              </PortfolioHeaderWrapper>
                <PortfolioLogoWrapper>
                  <img src={Logo} alt='Logo' />
                </PortfolioLogoWrapper>
            </PortfolioHeaderContent>
          </VerticalBox>
        </Box>
      </PortfolioHeader>
      {newProjects.length > 0 && (
        <PortfolioWrapper $bgColor="white" $paddingSize="sm">
          <Box>
            <Updates newProjectsList={newProjects} visualMode="page" />
          </Box>
        </PortfolioWrapper>
      )}
      <PortfolioWrapper>
        <Box>
          <PortfolioFilters
            stacks={portfolioData.stacks}
            projects={portfolioData.projects}
          />
        </Box>
      </PortfolioWrapper>
    </>
  );
};

const PortfolioHeader = styled(MotionSecondaryHeaderBackground)``;

const PortfolioHeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media screen and (${device.md}) {
    gap: 50px;
  }
  @media screen and (${device.sm}) {
    gap: 10px;
  }
`;

const PortfolioHeaderWrapper = styled(MotionText)`
  display: flex;
  flex-direction: column;
  gap: 40px;
  max-width: 60%;
  @media screen and (${device.lg}) {
    span {
      font-size: 50px;
      line-height: 60px;
    }
  }
  @media screen and (${device.md}) {
    gap: 20px;
    max-width: none;
    span {
      font-size: 33px;
      line-height: 40px;
    }
    h4 {
      font-size: 18px;
      line-height: 24px;
    }
  }
  @media screen and (${device.sm}) {
    gap: 10px;
    span {
      font-size: 16px;
      line-height: 24px;
    }
    h4 {
      font-size: 12px;
      line-height: 18px;
    }
  }
`;

const PortfolioLogoWrapper = styled(MotionPortfolioHeaderLogo)`
  width: 243px;
  height: 243px;
  border-radius: 50%;
  border: 2px dashed ${COLORS.ACTIVE_ITEM};
  display: flex;
  align-items: center;
  flex: 0 0 auto;

  img {
    width: 250px;
    position: relative;
    top: -15px;
  }

  @media screen and (${device.lg}) {
    width: 200px;
    height: 200px;

    img {
      width: 100%;
      top: -5px;
    }
  }
  @media screen and (${device.bsm}) {
    width: 120px;
    height: 120px;
  }
`;

type TPortfolioWrapperBgColor = 'white' | 'gray';
type TPortfolioWrapperPaddingSize = 'sm' | 'md';

const PortfolioWrapper = styled.div<{
  $bgColor?: TPortfolioWrapperBgColor;
  $paddingSize?: TPortfolioWrapperPaddingSize;
}>`
  background-color: ${({ $bgColor = 'gray' }) =>
    $bgColor === 'white' ? COLORS.WHITE : COLORS.PORTFOLIO_BACKGROUND};
  padding: ${({ $paddingSize = 'md' }) =>
    $paddingSize === 'md' ? '80px 0 220px' : '40px 0'};
  @media screen and (${device.md}) {
    padding: 40px 0;
  }
`;

const ErrorWrapper = styled.div`
  padding: 20px 0;
`;
